import { useCallback, useState } from 'react'
import {
  getAvailableDates,
  getAvailableTimeSlots,
  TimeSlotValue,
} from '../services/cinch-servicing'
import { AppointmentType } from '../types'
import { datadogRum } from '@datadog/browser-rum'

export const useFetchAppointmentAvailability = () => {
  const [loading, setLoading] = useState(false)
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false)
  const [appointmentDates, setAppointmentDates] = useState<string[]>()
  const [appointmentTypeId, setAppointmentTypeId] = useState<number>(5)
  const [timeSlots, setTimeSlots] = useState<TimeSlotValue[]>()
  const [error, setError] = useState<Error | null>(null)

  const fetchAppointmentDates = useCallback(
    async (
      dealerId: string | undefined,
      serviceOptionsSelectedNames: string | undefined,
      appointmentType: AppointmentType,
    ) => {
      if (!dealerId || !serviceOptionsSelectedNames || !appointmentType) {
        datadogRum.addError(
          new Error('Cinch Servicing: Select DateTime Error'),
          {
            cinchServicing: true,
            dealerId,
            serviceOptionsSelectedNames,
            appointmentType,
          },
        )
        setLoading(false)
        setError(new Error('Missing value'))
        return
      }

      setLoading(true)
      setError(null)

      try {
        const dates = await getAvailableDates({
          dealerId,
          serviceOptions: serviceOptionsSelectedNames,
          appointmentType,
        })
        if (dates && dates.length > 0) {
          setAppointmentDates(dates[0].datesAvailable)
          setAppointmentTypeId(dates[0].id)
          setError(null)
        } else {
          datadogRum.addError(
            new Error('Cinch Servicing: Get Available Dates None Available'),
            {
              cinchServicing: true,
              dealerId,
              serviceOptionsSelectedNames,
              appointmentType,
            },
          )
          setError(new Error('Failed to get appointment dates'))
        }
      } catch (error) {
        setError(error as Error)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const fetchAppointmentTimes = useCallback(
    async (
      dealerId: string | undefined,
      appointmentTypeId: number,
      date: string,
    ) => {
      if (!dealerId || !appointmentTypeId || !date) {
        datadogRum.addError(
          new Error('Cinch Servicing: Select TimeSlot Error'),
          { cinchServicing: true, dealerId, appointmentTypeId, date },
        )
        setTimeSlotsLoading(false)
        setError(new Error('Missing value'))
        return
      }

      setTimeSlotsLoading(true)
      setError(null)

      try {
        const availableTimes = await getAvailableTimeSlots({
          dealerId,
          appointmentTypeId,
          date,
        })
        if (
          availableTimes &&
          availableTimes.timeSlots &&
          availableTimes.timeSlots.length > 0
        ) {
          setTimeSlots(availableTimes.timeSlots)
          setError(null)
        } else {
          datadogRum.addError(
            new Error('Cinch Servicing: TimeSlots None Available'),
            { cinchServicing: true, dealerId, appointmentTypeId, date },
          )
          setError(new Error('Failed to get time slots'))
        }
      } catch (error) {
        setError(error as Error)
      } finally {
        setTimeSlotsLoading(false)
      }
    },
    [],
  )

  return {
    fetchAppointmentDates,
    fetchAppointmentTimes,
    appointmentDates,
    appointmentTypeId,
    timeSlots,
    timeSlotsLoading,
    loading,
    error,
  }
}
